import type { OrderItem, SelectedItemInfo } from '@/types';

/**
 * `extra` 그룹의 체크박스 옵션에서 "분해"/"조립" 선택 여부를 판정.
 *
 * 데이터 구조:
 * - items.csv 의 `extra_name` 이 `"...분해/조립"` 인 행 (예: 침대 프레임, 쏘서, 미끄럼틀).
 * - 체크박스 타입 → `options.extraValue` 가 선택된 라벨 문자열 배열.
 *
 * 적용 규칙:
 * - "분해" 선택 → 출발지(수거)가 반드시 집안 수거여야 함.
 * - "조립" 선택 → 도착지(배송)가 반드시 집안 배송이어야 함.
 */
function getExtraValues(item: { options?: Record<string, unknown> }): string[] {
  const extras = item.options?.extraValue;
  if (!Array.isArray(extras)) return [];
  return extras.filter((v): v is string => typeof v === 'string');
}

export function itemRequiresInsidePickup(item: { options?: Record<string, unknown> }): boolean {
  return getExtraValues(item).some((v) => v.includes('분해'));
}

export function itemRequiresInsideDelivery(item: { options?: Record<string, unknown> }): boolean {
  return getExtraValues(item).some((v) => v.includes('조립'));
}

/** 주문 전체에 "분해" 선택 물품이 한 건이라도 있으면 true (출발지 = 집안 수거 강제). */
export function orderRequiresInsidePickup(
  items: OrderItem[],
  itemInfo: SelectedItemInfo,
): boolean {
  for (const item of items) {
    if (itemRequiresInsidePickup(item)) return true;
  }
  if (itemInfo.itemId && itemRequiresInsidePickup({ options: itemInfo.options })) {
    return true;
  }
  return false;
}

/** 주문 전체에 "조립" 선택 물품이 한 건이라도 있으면 true (도착지 = 집안 배송 강제). */
export function orderRequiresInsideDelivery(
  items: OrderItem[],
  itemInfo: SelectedItemInfo,
): boolean {
  for (const item of items) {
    if (itemRequiresInsideDelivery(item)) return true;
  }
  if (itemInfo.itemId && itemRequiresInsideDelivery({ options: itemInfo.options })) {
    return true;
  }
  return false;
}
